import { API_BASE_URL } from "../../../common/api/config.js";

/**
 * Verbindung zum Chat-WebSocket.
 *
 * Das Token steht nicht in der Adresse, sondern geht als erste Nachricht über die
 * Verbindung — eine URL landet in Logs, im Verlauf und in Referrern. Bricht die
 * Verbindung ab, wird mit wachsendem Abstand neu verbunden.
 */

export type SocketEvent = { type: string; payload?: unknown };

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

export interface ChatSocket {
  subscribe(channelId: string): void;
  unsubscribe(channelId: string): void;
  close(): void;
}

export function openChatSocket(input: {
  getToken: () => string | null;
  onEvent: (event: SocketEvent) => void;
  onStatus?: (connected: boolean) => void;
}): ChatSocket {
  const channels = new Set<string>();
  let socket: WebSocket | null = null;
  let attempt = 0;
  let closed = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const url = (): string => {
    const absolute = `${window.location.origin}${API_BASE_URL}`;
    return `${absolute.replace(/^http/, "ws").replace(/\/$/, "")}/chat/ws`;
  };

  const connect = (): void => {
    if (closed) {
      return;
    }
    const token = input.getToken();
    if (!token) {
      schedule();
      return;
    }

    socket = new WebSocket(url());

    socket.addEventListener("open", () => {
      socket?.send(JSON.stringify({ type: "auth", token }));
    });

    socket.addEventListener("message", (message) => {
      let event: SocketEvent;
      try {
        event = JSON.parse(String(message.data)) as SocketEvent;
      } catch {
        return;
      }
      if (event.type === "auth.ok") {
        attempt = 0;
        input.onStatus?.(true);
        for (const channelId of channels) {
          socket?.send(JSON.stringify({ type: "subscribe", channelId }));
        }
        return;
      }
      input.onEvent(event);
    });

    socket.addEventListener("close", () => {
      input.onStatus?.(false);
      socket = null;
      schedule();
    });

    socket.addEventListener("error", () => {
      socket?.close();
    });
  };

  const schedule = (): void => {
    if (closed || timer) {
      return;
    }
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
    attempt += 1;
    timer = setTimeout(() => {
      timer = null;
      connect();
    }, delay);
  };

  connect();

  return {
    subscribe(channelId) {
      channels.add(channelId);
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: "subscribe", channelId }));
      }
    },
    unsubscribe(channelId) {
      channels.delete(channelId);
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: "unsubscribe", channelId }));
      }
    },
    close() {
      closed = true;
      if (timer) {
        clearTimeout(timer);
      }
      socket?.close();
    },
  };
}
